import { useCallback, useEffect, useRef, useState } from "react";

import { Snackbar } from "@js/components/snackbar";
import type { ManagedJob } from "@js/types/jobs";

import { useGenerateInterviewQuestionMutation } from "../../api";
import { JobInterviewQuestionSnackbarContent } from "../../components/job-interview-question-snackbar-content";

const errorMessage = {
  interview_question: {
    title: "AI generated question error",
    message:
      "Unable to automatically generate questions, please type the questions you’d like to ask.",
  },
  interview_criteria: {
    title: "AI grading criteria error",
    message:
      "Unable to automatically generate grading criteria for, please specify your own grading criteria.",
  },
};

export const useGenerateAiInterviewData = ({
  job,
  isJobLoading,
  refetchJob,
}: {
  job: ManagedJob | undefined;
  isJobLoading: boolean;
  refetchJob: () => Promise<ManagedJob>;
}) => {
  // we need additional flag for the moment after launching the genetation query, when the job refetch is in progress and task_in_progress is still false
  // ... as we cannot use isJobLoading (isFetching) in isGeneratingInterviewData, because it would hide the form in case of an error during submission
  const [isGenerating, setIsGenerating] = useState(false);
  const [enableInterview, setEnableInterview] = useState(false);
  const [generateInterviewQuestion, { isLoading }] =
    useGenerateInterviewQuestionMutation();

  const isGenerationTaskInProgress =
    !!job?.generating_ai_interview_data_task_in_progress;
  const isGeneratingInterviewData =
    isLoading || isGenerationTaskInProgress || isGenerating;

  const timeoutRef = useRef<number>(undefined);
  const pollJob = useCallback(async () => {
    try {
      const response = await refetchJob();
      const isStillGenerating =
        response.generating_ai_interview_data_task_in_progress;
      if (!isStillGenerating) {
        setIsGenerating(false);
        return;
      }

      // eslint-disable-next-line react-hooks/immutability
      timeoutRef.current = window.setTimeout(() => pollJob(), 3000);
    } catch (_error) {
      Snackbar.error("Something went wrong during job sync.");
    }
  }, [refetchJob]);

  const isPollingInitializedRef = useRef(false);
  useEffect(() => {
    if (!job || isPollingInitializedRef.current) {
      return;
    }

    if (!job.generating_ai_interview_data_task_in_progress) {
      return;
    }

    isPollingInitializedRef.current = true;
    timeoutRef.current = window.setTimeout(() => pollJob(), 2000);
  }, [job, pollJob]);

  useEffect(() => {
    return () => {
      clearTimeout(timeoutRef.current);
    };
  }, []);

  const snackbarError = ({
    title,
    message,
  }: {
    title: string;
    message: string;
  }) => {
    Snackbar.custom(
      <JobInterviewQuestionSnackbarContent header={title} message={message} />,
      {
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "right",
        },
      },
    );
  };

  const enableAndGenerateQuestion = useCallback(
    async (jobId: number) => {
      setIsGenerating(true);
      generateInterviewQuestion({ jobId })
        .unwrap()
        .then(() => {
          setEnableInterview(true);
        })
        .catch(() => {
          snackbarError(errorMessage.interview_question);
        });
    },
    [generateInterviewQuestion],
  );

  useEffect(() => {
    if (
      !job ||
      !job.user_can_manage ||
      isJobLoading ||
      job.generating_ai_interview_data_task_in_progress ||
      enableInterview // already triggered generation
    ) {
      return;
    }

    const shouldTriggerDataGeneration =
      !job.interview_questions?.length || !job.interview_criteria?.length;
    if (!job?.is_ai_interview_enabled && shouldTriggerDataGeneration) {
      void enableAndGenerateQuestion(job.id);
    }
  }, [job, enableAndGenerateQuestion, isJobLoading, enableInterview]);

  useEffect(() => {
    if (
      !job ||
      !job.user_can_manage ||
      !job.is_ai_interview_enabled ||
      isJobLoading ||
      job.generating_ai_interview_data_task_in_progress
    ) {
      return;
    }

    if (job?.interview_questions?.length === 0) {
      snackbarError(errorMessage.interview_question);
    }
    if (job?.interview_criteria?.length === 0) {
      snackbarError(errorMessage.interview_criteria);
    }
  }, [isJobLoading, job]);

  return {
    isGeneratingInterviewData,
    enableInterview,
  };
};
